import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Renders up to 6 related servers from the same primary category.
 * Purpose: strong internal linking for SEO (helps Google crawl depth) and
 * keeps visitors moving between listings instead of bouncing.
 *
 * Silent no-op if the server has no category or the API errors.
 */
export default function RelatedServers({ server }) {
  const [items, setItems] = useState(null);
  const category = (server?.categories || [])[0];

  useEffect(() => {
    if (!category || !server?.id) {
      setItems([]);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/servers", {
          params: { category, sort: "members", limit: 8 },
        });
        if (!alive) return;
        // Exclude the current server + take the top 6.
        const list = (data.items || [])
          .filter((s) => s.id !== server.id)
          .slice(0, 6);
        setItems(list);
      } catch {
        if (alive) setItems([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [category, server?.id]);

  if (!items || items.length === 0) return null;

  return (
    <section className="mt-14" data-testid="related-servers">
      <div className="flex items-end justify-between gap-3">
        <h2 className="font-heading text-xl sm:text-2xl font-black">
          More {category ? categoryLabel(category) : "Discord"} servers
        </h2>
        <Link
          to={`/best/${category}-discord-servers`}
          className="text-sm text-neon-cyan hover:underline font-semibold"
        >
          See all →
        </Link>
      </div>
      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((s) => (
          <li key={s.id}>
            <Link
              to={`/servers/${s.slug || s.id}`}
              className="group flex items-center gap-3 h-16 px-3 rounded-xl border border-white/10 bg-[#0A0A0F] hover:border-blurple/40 hover:bg-blurple/[0.04] transition-colors"
              data-testid={`related-server-${s.id}`}
            >
              {s.icon_url ? (
                <img
                  src={s.icon_url}
                  alt=""
                  loading="lazy"
                  className="w-10 h-10 rounded-lg shrink-0 border border-white/10"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-blurple/20 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-white truncate group-hover:text-neon-cyan transition-colors">
                  {s.name}
                </div>
                <div className="text-[11px] text-zinc-500 tabular-nums">
                  {(s.member_count || 0).toLocaleString()} members
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

const CATEGORY_LABELS = {
  gaming: "gaming",
  community: "community",
  anime: "anime",
  tech: "tech",
  programming: "programming",
  music: "music",
  art: "art",
  education: "education",
  books: "books & writing",
  roleplay: "roleplay",
  crypto: "crypto",
  esports: "esports",
  memes: "meme",
};
function categoryLabel(id) {
  return CATEGORY_LABELS[id] || id;
}
